import { useEffect, useMemo, useState } from "react";
import {
  Box,
  IconButton,
  Grid,
  Chip,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import FeaturedPlayListOutlinedIcon from "@mui/icons-material/FeaturedPlayListOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";

import DatagridCustom from "../General/DatagridCustom";
import AppDialog from "../General/Dialog/AppDialog";
import { useAuth } from "../../context/AuthContext";
import { downloadLawyerDecree } from "../../services/LawyerService";
import { useToast } from "../../context/ToastContext";

export default function LawyersTable({
  lawyers,
  onDelete,
  onEdit,
  onUpdatePassword,
  onPauseUpdates,
  onResumeUpdates,
  loading = false,
}) {
  const { hasPermission } = useAuth();
  const { showToast } = useToast();

  const [openPdfDialog, setOpenPdfDialog] = useState(false);
  const [selectedLawyer, setSelectedLawyer] = useState(null);
  const [selectedPdfPath, setSelectedPdfPath] = useState("");
  const [pdfBlobUrl, setPdfBlobUrl] = useState("");
  const [loadingPdf, setLoadingPdf] = useState(false);

  const formatTaxCondition = (value) => {
    switch (value) {
      case "monotributista":
        return "Monotributista";
      case "responsable_inscripto":
        return "Responsable inscripto";
      case "no_inscripto":
        return "No inscripto";
      default:
        return "-";
    }
  };

  const getLawyerPdfOptions = (lawyer) => {
    if (!lawyer) return [];

    return [
      {
        label: "Decreto",
        value: lawyer.decree_file || "",
      },
      {
        label: "Constancia ARCA",
        value: lawyer.arca_certificate_file || "",
      },
    ];
  };

  const pdfOptions = useMemo(
    () => getLawyerPdfOptions(selectedLawyer),
    [selectedLawyer],
  );

  const cleanupPdfUrl = () => {
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
    }
  };

  const fetchPdfPreview = async (path) => {
    cleanupPdfUrl();
    setPdfBlobUrl("");

    if (!path) {
      showToast("El abogado no tiene archivo cargado para esta opción.", "info");
      return;
    }

    try {
      setLoadingPdf(true);
      const response = await downloadLawyerDecree(path);

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      setPdfBlobUrl(url);
    } catch (error) {
      console.error(error);
      showToast("No se pudo cargar el archivo PDF.", "error");
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleOpenPdfDialog = async (lawyer) => {
    setSelectedLawyer(lawyer);
    setOpenPdfDialog(true);
    cleanupPdfUrl();

    const options = getLawyerPdfOptions(lawyer);
    const firstAvailable = options.find((item) => item.value);

    if (firstAvailable) {
      setSelectedPdfPath(firstAvailable.value);
      await fetchPdfPreview(firstAvailable.value);
    } else {
      setSelectedPdfPath("");
      showToast("Este abogado no tiene archivos PDF cargados.", "info");
    }
  };

  const handleClosePdfDialog = () => {
    cleanupPdfUrl();
    setOpenPdfDialog(false);
    setSelectedLawyer(null);
    setSelectedPdfPath("");
    setPdfBlobUrl("");
    setLoadingPdf(false);
  };

  const handleChangePdf = async (event) => {
    const path = event.target.value;
    setSelectedPdfPath(path);
    await fetchPdfPreview(path);
  };

  useEffect(() => {
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [pdfBlobUrl]);

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5 },
    { field: "name", headerName: "Nombre", flex: 1 },
    { field: "lastname", headerName: "Apellido", flex: 1 },
    { field: "enrollment", headerName: "Usuario SAC", flex: 1 },
    { field: "document", headerName: "Matrícula", flex: 1 },
    {
      field: "tax_condition",
      headerName: "Condición tributaria",
      flex: 1.2,
      renderCell: (params) => {
        const label = formatTaxCondition(params.row.tax_condition);

        if (label === "-") return "-";

        return (
          <Chip
            label={label}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        );
      },
    },
    { field: "number_decree", headerName: "Número de Decreto", flex: 1 },
    { field: "date_decree", headerName: "Fecha de Decreto", flex: 1 },
    {
      field: "updates_paused",
      headerName: "Actualizaciones",
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={params.row.updates_paused ? "Pausadas" : "Activas"}
          size="small"
          color={params.row.updates_paused ? "warning" : "success"}
          variant={params.row.updates_paused ? "filled" : "outlined"}
          sx={{ fontWeight: 600 }}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Acciones",
      sortable: false,
      flex: 2.5,
      renderCell: (params) => (
        <>
          <IconButton
            color="primary"
            onClick={() => onEdit(params.row)}
            disabled={!hasPermission("actualizar abogado")}
          >
            <EditIcon />
          </IconButton>

          <IconButton
            color="secondary"
            onClick={() => onUpdatePassword(params.row.id)}
            disabled={!hasPermission("cambiar contrasenia abogado")}
          >
            <LockOpenOutlinedIcon />
          </IconButton>

          <IconButton
            color="error"
            onClick={() => handleOpenPdfDialog(params.row)}
            disabled={!hasPermission("descargar decreto abogado")}
          >
            <PictureAsPdfIcon />
          </IconButton>

          {params.row.updates_paused ? (
            <Tooltip title="Reanudar actualizaciones">
              <span>
                <IconButton
                  color="success"
                  onClick={() => onResumeUpdates(params.row)}
                  disabled={!hasPermission("pausar actualizacion abogado")}
                >
                  <PlayCircleOutlineIcon />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <Tooltip title="Pausar actualizaciones">
              <span>
                <IconButton
                  color="warning"
                  onClick={() => onPauseUpdates(params.row)}
                  disabled={!hasPermission("pausar actualizacion abogado")}
                >
                  <PauseCircleOutlineIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}

          <IconButton color="error" onClick={() => onDelete(params.row.id)}>
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <>
      <Grid container justifyContent="center">
      </Grid>

      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <DatagridCustom
          rows={lawyers}
          columns={columns}
          title="Listado de Abogados"
          description="Administrar los abogados registrados."
          icon={<FeaturedPlayListOutlinedIcon sx={{ fontSize: 32 }} />}
          getRowId={(row) => row.id}
          loading={loading}
        />
      </Box>

      <AppDialog
        open={openPdfDialog}
        onClose={handleClosePdfDialog}
        title={
          selectedLawyer
            ? `PDFs de ${selectedLawyer.name} ${selectedLawyer.lastname}`
            : "PDFs del abogado"
        }
        description="Ver y descargar los archivos PDF asociados a este abogado."
        icon={<PictureAsPdfIcon />}
        maxWidth="lg"
        fullWidth
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 12 }}>
            <FormControl fullWidth>
              <InputLabel id="lawyer-pdf-select-label">Archivo PDF</InputLabel>
              <Select
                labelId="lawyer-pdf-select-label"
                value={selectedPdfPath}
                label="Archivo PDF"
                onChange={handleChangePdf}
              >
                {pdfOptions.map((option) => (
                  <MenuItem
                    key={option.label}
                    value={option.value}
                    disabled={!option.value}
                  >
                    {option.label.toUpperCase()}
                    {!option.value ? " (SIN ARCHIVO)" : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12 }}>
            {loadingPdf && (
              <Box
                sx={{
                  minHeight: 420,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                }}
              >
                <CircularProgress />
              </Box>
            )}

            {!loadingPdf && pdfBlobUrl && (
              <Box
                sx={{
                  width: "100%",
                  height: { xs: 420, md: 700 },
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  overflow: "hidden",
                  bgcolor: "#f8f9fb",
                }}
              >
                <iframe
                  src={pdfBlobUrl}
                  title="Vista previa PDF"
                  width="100%"
                  height="100%"
                  style={{ border: "none" }}
                />
              </Box>
            )}

            {!loadingPdf && !pdfBlobUrl && (
              <Box
                sx={{
                  minHeight: 220,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px dashed",
                  borderColor: "divider",
                  borderRadius: 2,
                  px: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Seleccioná un archivo para visualizarlo.
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </AppDialog>
    </>
  );
}
